import React, { useState } from "react";

import withStyles from "@mui/styles/withStyles";
import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";


import Button from "components/CustomButtons/Button";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import CustomDatetimeAdapter from "../components/CustomDatetimeAdapter";
import CustomInputAdapter from "../components/CustomInputAdapter";
import CustomDropdownAdapter from "customComponents/CustomDropdownAdapter/CustomDropdownAdapter";
import LocationModal from "../LocationModal";

import modalStyle from "assets/jss/material-kit-pro-react/modalStyle";
import PropTypes from "prop-types";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import FieldAdapter from "../components/FieldAdapter";

import ImageSelectAdapter from "../components/ImageSelectAdapter";
import EditorAdapter from "../components/EditorAdapter";
import SwitchAdapter from "../components/SwitchAdapter";
import SponsorImageSelectAdapter from "../components/SponsorImageSelectAdapter";
import { FieldArray } from "react-final-form-arrays";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardFooter from "components/Card/CardFooter";
import { getImageURL } from "utils/Utils";

const style = (theme) => ({
    ...modalStyle(theme),
    modalRootExample: {
        "& > div:first-child": {
            display: "none",
        },
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    removeButton: {
        display: "flex",
        justifyContent: "flex-end",
    },
});

const Transition = React.forwardRef((props, ref) => (
    <Slide direction="down" ref={ref} {...props} />
));

/**
 * URL-Eingabe mit automatischem https://-Prefix beim Verlassen des Feldes.
 * Validate fängt den Fall ab, wenn das Feld noch nie verlassen wurde.
 */
const SponsorUrlAdapter = ({ input, ...rest }) => (
    <CustomInputAdapter
        input={{
            ...input,
            onBlur: (e) => {
                const val = (input.value ?? "").trim();
                if (val && !/^https?:\/\//i.test(val)) {
                    input.onChange(`https://${val}`);
                }
                input.onBlur(e);
            },
        }}
        {...rest}
    />
);

const EventModal = (props) => {
    const [open, setOpen] = useState(false);
    const [event, setEvent] = useState();
    const [imageURI, setImageURI] = useState();

    const {
        classes,
        button,
        event: initialEvent,
        onSubmit: handleSubmitProp,
        locations,
        createLocation,
        isTenantAdmin,
    } = props;


    const onSubmit = async (values) => {
        const { imageFile } = values;
        // Only include fields the form manages — prevents MobX-internal properties
        // (rootStore, courses, …) from being sent to the API and triggering auth errors
        // when the user only has partial field-level update permissions.
        const sponsorImageFiles = (values.sponsors || []).map((s) => s.logoFile ?? null);
        const eventData = {
            // id + group fields only exist in edit mode (from initialValues).
            // For create, they're undefined — omit them so the Amplify client
            // doesn't serialize them as null and trip GraphQL's NonNull check.
            ...(values.id != null && { id: values.id }),
            ...(values.clubID != null && { clubID: values.clubID }),
            ...(values.tenantAdminGroup != null && { tenantAdminGroup: values.tenantAdminGroup }),
            ...(values.clubAdminGroup != null && { clubAdminGroup: values.clubAdminGroup }),
            name: values.name,
            tagline: values.tagline,
            description: values.description,
            descriptionAsHtml: values.descriptionAsHtml,
            details: values.details,
            image: values.image,
            icon: values.icon,
            schedule: values.schedule,
            location: values.location,
            // Strip File objects and __typename before sending to API.
            // Auto-prefix URLs that are missing a protocol (e.g. "example.com" → "https://example.com").
            sponsors: (values.sponsors || []).map(({ logoFile, __typename, ...s }) => ({
                ...s,
                url: s.url && !/^https?:\/\//i.test(s.url) ? `https://${s.url}` : (s.url || ''),
            })),
            ...(isTenantAdmin ? { highlight: values.highlight } : {}),
        };
        await handleSubmitProp(eventData, imageFile, sponsorImageFiles);
        handleClose();
    };

    const handleClickOpen = async () => {
        setEvent({
            ...initialEvent,
            description: initialEvent ? initialEvent.descriptionAsHtml : "",
            // Convert MobX ObservableArray + observable sponsor objects to plain JS so
            // react-final-form's FieldArray picks them up as initial values.
            // Also strip __typename and inject logoFile: null (no File yet).
            sponsors: (initialEvent?.sponsors || []).map(
                // eslint-disable-next-line no-unused-vars
                ({ __typename, logoFile: _lf, ...s }) => ({ ...s, logoFile: null }),
            ),
        });
        if (initialEvent && initialEvent.image) setImageURI(initialEvent.image);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };


    return (
        <div>
            <div onClick={() => handleClickOpen()}>{button}</div>
            {open && (
                <Dialog
                    classes={{
                        root: `${classes.modalRoot} ${classes.modalRootExample}`,
                        paper: `${classes.modal} ${classes.modalLarge}`,
                    }}
                    open={open}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={() => handleClose("scrollingModal")}
                    aria-labelledby="classic-modal-slide-title"
                    aria-describedby="classic-modal-slide-description"
                >
                    <Form
                        onSubmit={onSubmit}
                        initialValues={event}
                        mutators={{ ...arrayMutators }}
                        render={({ handleSubmit, form, submitting, pristine, values }) => (
                            <form onSubmit={handleSubmit}>
                                <DialogTitle
                                    id="classic-modal-slide-title"
                                    className={classes.modalHeader}
                                >
                                    <Button
                                        simple
                                        className={classes.modalCloseButton}
                                        key="close"
                                        aria-label="Close"
                                        onClick={() => handleClose("scrollingModal")}
                                    >
                                        <i className="fas fa-times" />
                                    </Button>
                                    <h4 className={classes.modalTitle}>
                                        {values.name ? values.name : "Neue Veranstaltung"}
                                    </h4>
                                </DialogTitle>
                                <DialogContent
                                    id="classic-modal-slide-description"
                                    className={classes.modalBody}
                                >
                                    <GridContainer>
                                        <GridItem>
                                            <FieldAdapter
                                                name="imageFile"
                                                component={ImageSelectAdapter}
                                                type="file"
                                                id="imageFile"
                                                imagePath={imageURI}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <FieldAdapter
                                                name="name"
                                                component={CustomInputAdapter}
                                                type="text"
                                                placeholder="Name"
                                                label="Name"
                                                validate={(value) => !value && "Eingabe erforderlich"}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <FieldAdapter
                                                name="tagline"
                                                component={CustomInputAdapter}
                                                type="text"
                                                placeholder="Tagline"
                                                label="Tagline"
                                                inputProps={{
                                                    multiline: true,
                                                    maxRows: 10,
                                                }}
                                            />
                                        </GridItem>
                                        {isTenantAdmin && (
                                            <GridItem xs={12}>
                                                <FieldAdapter
                                                    name="highlight"
                                                    component={SwitchAdapter}
                                                    type="checkbox"
                                                    label="Highlight (Sport im Park)"
                                                />
                                            </GridItem>
                                        )}
                                        <GridItem>
                                            <FieldAdapter
                                                name="description"
                                                component={EditorAdapter}
                                                label="Beschreibung"
                                                id="description"
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <FieldAdapter
                                                name="details"
                                                component={CustomInputAdapter}
                                                type="text"
                                                placeholder="Details"
                                                label="Details"
                                                inputProps={{
                                                    multiline: true,
                                                    maxRows: 10,
                                                }}
                                            />
                                        </GridItem>

                                        <GridItem xs={12} sm={6} md={6} lg={6}>
                                            <h4>Datum</h4>
                                        </GridItem>
                                        <GridItem xs={12}>
                                            <GridContainer>
                                                <GridItem xs={6} sm={6} md={6} lg={6}>
                                                    <FieldAdapter
                                                        name={"schedule.from"}
                                                        component={CustomDatetimeAdapter}
                                                        label="Start"
                                                        dateFormat={"YYYY-MM-DD"}
                                                        timeFormat={false}
                                                        validate={(value) => {
                                                            const match = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
                                                            if (!value) {
                                                                return "Eingabe erforderlich";
                                                            } else if (!match.test(value)) {
                                                                return "Datum muss das Format YYYY-MM-DD haben";
                                                            }
                                                        }}
                                                    />
                                                </GridItem>
                                                <GridItem xs={6} sm={6} md={6} lg={6}>
                                                    <FieldAdapter
                                                        name={"schedule.to"}
                                                        component={CustomDatetimeAdapter}
                                                        label="Ende"
                                                        dateFormat={"YYYY-MM-DD"}
                                                        timeFormat={false}
                                                        validate={(value) => {
                                                          const match = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
                                                          if (!value) {
                                                              return "Eingabe erforderlich";
                                                          } else if (!match.test(value)) {
                                                              return "Datum muss das Format YYYY-MM-DD haben";
                                                          }
                                                          if (values.schedule?.from && value && value < values.schedule.from) {
                                                              return "Enddatum muss nach dem Startdatum liegen";
                                                          }
                                                        }}
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>

                                        <GridItem xs={12} sm={6} md={6} lg={6}>
                                            <h4>Sportstätte</h4>
                                        </GridItem>
                                        <GridItem xs={12} sm={6} md={6} lg={6}>
                                            <LocationModal
                                                onSubmit={createLocation}
                                                button={(onClick) => {
                                                    return (
                                                        <Button
                                                            color="primary"
                                                            simple
                                                            onClick={() => onClick()}
                                                        >
                                                            <i className="fas fa-plus" /> Neue Sportstätte
                                                        </Button>
                                                    );
                                                }}
                                            />
                                        </GridItem>
                                        <GridItem xs={12}>
                                            <FieldAdapter
                                                name="location"
                                                component={CustomDropdownAdapter}
                                                type="text"
                                                label="Sportstätte auswählen"
                                                placeholder="Sportstätte auswählen"
                                                dropDownList={(locations || [])
                                                    .filter((item) => item && item.name)
                                                    .sort((a, b) => a.name.localeCompare(b.name))}
                                                id="location"
                                                getKey={(item) => item?.id}
                                                getValue={(item) => item}
                                                getName={(item) => item?.name}
                                            />
                                        </GridItem>


                                        <GridItem xs={12} style={{ marginTop: 24, borderTop: "1px solid #eee", paddingTop: 8 }}>
                                            <h4>Sponsoren</h4>
                                        </GridItem>

                                        <GridItem xs={12}>
                                            <FieldArray name="sponsors">
                                                {({ fields }) => (
                                                    <>
                                                        {fields.map((name, index) => {
                                                            const sv = values.sponsors?.[index] ?? {};
                                                            const previewSrc = sv.logoFile
                                                                ? URL.createObjectURL(sv.logoFile)
                                                                : sv.logo ?? null;

                                                            return (
                                                                <GridContainer
                                                                    key={name}
                                                                    style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid #eee" }}
                                                                >
                                                                    <GridItem xs={12} sm={previewSrc ? 6 : 12}>
                                                                        <FieldAdapter
                                                                            name={`${name}.logoFile`}
                                                                            component={SponsorImageSelectAdapter}
                                                                            type="file"
                                                                            logoPath={sv.logo ?? undefined}
                                                                        />
                                                                    </GridItem>

                                                                    {previewSrc && (
                                                                        <GridItem xs={12} sm={6}>
                                                                            <Card profile plain style={{ marginTop: 0 }}>
                                                                                <CardHeader image plain noShadow>
                                                                                    <a href="#" onClick={(e) => e.preventDefault()}>
                                                                                        <img src={previewSrc} alt={sv.name} style={{ background: "white" }} />
                                                                                    </a>
                                                                                </CardHeader>
                                                                                <CardBody>
                                                                                    <h4>{sv.name || "Sponsorname"}</h4>
                                                                                </CardBody>
                                                                                <CardFooter profile style={{ justifyContent: "center" }}>
                                                                                    <Button round color="info" href="#" onClick={(e) => e.preventDefault()}>
                                                                                        {sv.callToAction || "Mehr erfahren"}
                                                                                    </Button>
                                                                                </CardFooter>
                                                                            </Card>
                                                                        </GridItem>
                                                                    )}

                                                                    <GridItem xs={12} sm={6}>
                                                                        <FieldAdapter
                                                                            name={`${name}.name`}
                                                                            component={CustomInputAdapter}
                                                                            type="text"
                                                                            label="Sponsorname"
                                                                            validate={(v) => !v && "Eingabe erforderlich"}
                                                                        />
                                                                    </GridItem>
                                                                    <GridItem xs={12} sm={6}>
                                                                        <FieldAdapter
                                                                            name={`${name}.callToAction`}
                                                                            component={CustomInputAdapter}
                                                                            type="text"
                                                                            label="Button-Text"
                                                                            placeholder="z.B. Mehr erfahren"
                                                                            inputProps={{ maxLength: 40 }}
                                                                        />
                                                                    </GridItem>
                                                                    <GridItem xs={12}>
                                                                        <FieldAdapter
                                                                            name={`${name}.url`}
                                                                            component={SponsorUrlAdapter}
                                                                            type="text"
                                                                            label="Ziel-URL"
                                                                            placeholder="https://..."
                                                                            validate={(v) =>
                                                                                v && !/^https?:\/\//i.test(v)
                                                                                    ? "URL muss mit https:// beginnen"
                                                                                    : undefined
                                                                            }
                                                                        />
                                                                    </GridItem>

                                                                    <GridItem xs={12} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                                        <div>
                                                                            <Button
                                                                                simple
                                                                                color="primary"
                                                                                disabled={index === 0}
                                                                                onClick={() => fields.move(index, index - 1)}
                                                                            >
                                                                                <i className="fas fa-arrow-up" /> Hoch
                                                                            </Button>
                                                                            <Button
                                                                                simple
                                                                                color="primary"
                                                                                disabled={index === fields.length - 1}
                                                                                onClick={() => fields.move(index, index + 1)}
                                                                            >
                                                                                <i className="fas fa-arrow-down" /> Runter
                                                                            </Button>
                                                                        </div>
                                                                        <Button
                                                                            color="danger"
                                                                            simple
                                                                            onClick={() => fields.remove(index)}
                                                                        >
                                                                            <i className="fas fa-trash" /> Sponsor entfernen
                                                                        </Button>
                                                                    </GridItem>
                                                                </GridContainer>
                                                            );
                                                        })}

                                                        <Button
                                                            color="primary"
                                                            simple
                                                            onClick={() => fields.push({ name: "", url: "", callToAction: "", logo: "", logoFile: null })}
                                                        >
                                                            <i className="fas fa-plus" /> Sponsor hinzufügen
                                                        </Button>
                                                    </>
                                                )}
                                            </FieldArray>
                                        </GridItem>

                                    </GridContainer>
                                </DialogContent>
                                <DialogActions className={classes.modalFooter}>
                                    <Button
                                        onClick={form.reset}
                                        disabled={submitting || pristine}
                                        color="secondary"
                                    >
                                        Zurücksetzen
                                    </Button>
                                    <Button color="primary" type="submit" disabled={submitting}>
                                        Speichern
                                    </Button>
                                </DialogActions>
                            </form>
                        )}
                    />
                </Dialog>
            )}
        </div>
    );
};

EventModal.propTypes = {
    classes: PropTypes.object,
    event: PropTypes.object,
    onSubmit: PropTypes.func.isRequired,
    button: PropTypes.node,
    locations: PropTypes.array,
    createLocation: PropTypes.func,
    isTenantAdmin: PropTypes.bool,
};

EventModal.defaultProps = {
    locations: [],
    event: {},
    isTenantAdmin: false,
};

export default withStyles(style)(EventModal);